import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ImportByTemplate from 'components/global/ImportByTemplate';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';

import { ADVANCE_WMS_MODULE, AUTOCOMPLETE, FORM_IMPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faDownload, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';

const EasyputForm = NiceModal.create(({ modalName }) => {
	const schema = useMemo(
		() =>
			yup.object().shape({
				fromBinID: yup.number().nullable().required(),
				toBinID: yup.number().nullable().required(),
				items: yup.array().of(
					yup.object().shape({
						id: yup.number().nullable().required(),
						sku: yup.string().nullable().required(),
						qty: yup.number().nullable().required()
					})
				)
			}),
		[]
	);
	const dispatch = useDispatch();
	const axios = useAxiosPrivate();

	const { showSuccess, showError } = useAlert();

	const { closeModal } = useModalRouter();

	const [loading, setLoading] = useState(false);

	const handleCloseModal = () => closeModal();

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			fromBinID: null,
			toBinID: null,
			items: [
				{
					id: null,
					sku: null,
					qty: null
				}
			]
		}
	});
	const {
		handleSubmit,
		setValue,
		getValues,
		watch,
		formState: { isDirty }
	} = formMethods;

	const { append, remove } = useFieldArray({
		control: formMethods.control,
		name: 'items'
	});

	const productImportTemplate = {
		icon: faDownload,
		title: 'importByTemplate',
		role: 'importByTemplate',
		downloadUrl: FORM_IMPORT.GET_FORM_IMPORT_PRODUCT_TEMPLATE,
		uploadUrl: FORM_IMPORT.IMPORT_PRODUCT_TEMPLATE,
		acceptedFileTypes: ['.xlsx', '.xls']
	};

	const tableData = watch('tableData');
	useEffect(() => {
		if (tableData) {
			// if you has default row
			const { productID, quantity } = watch('items[0]');

			if (!productID && !quantity) {
				remove(0);
			}

			const newProducts = tableData.map((item) => ({
				sku: item.SKU,
				id: item.productID,
				qty: item.quantity
			}));

			append(newProducts);

			const existingProducts = getValues('items');

			newProducts.forEach((item, index) => {
				if (index >= existingProducts.length) {
					['sku', 'id', 'qty'].forEach((field) => {
						setValue(`items[${index}].${field}`, item[field], {
							shouldDirty: true,
							shouldValidate: true
						});
					});
				}
			});
		}
	}, [tableData]);

	const onSubmit = async (data) => {
		setLoading(true);
		try {
			const response = await axios.post(ADVANCE_WMS_MODULE.EASY_PUTAWAY, data);
			if (response.status === 200) {
				dispatch(updateDynamicListData(DYNAMICLISTS.PUTAWAY_LIST));
				showSuccess();
				handleCloseModal();
			}
		} catch (error) {
			if (error.response.data?.message && JSON.parse(error.response.data?.message).langKey) {
				showError({ title: 'Error', message: JSON.parse(error.response.data?.message).langKey });
			} else if (error.response.data?.message && JSON.parse(error.response.data?.message).message) {
				showError({ title: 'Error', message: JSON.parse(error.response.data?.message).message });
			} else {
				showError();
			}
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	};

	const binAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const defaultColSpan = {
		md: 6
	};
	const productAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'SKU', width: 200 },
			{ accessor: 'name', width: 200 }
		],
		[]
	);
	const columns = useMemo(
		() => [
			{
				header: 'nameAndSku',
				accessorKey: 'sku',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						name={`items[${index}].id`}
						value={watch(`items[${index}].id`)}
						setValue={(value) => {
							setValue(`items[${index}].id`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`items[${index}].sku`, value?.SKU ?? null);
						}}
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						columns={productAutocompleteColumns}
						selectionLabelAccessor={['SKU', 'name']}
						viewMode
					/>
				),
				editCell: ({ row: { index } }) => (
					<AutoComplete
						name={`items[${index}].id`}
						value={watch(`items[${index}].id`)}
						setValue={(value) => {
							setValue(`items[${index}].id`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`items[${index}].sku`, value?.SKU ?? null, {
								shouldDirty: true,
								shouldValidate: true
							});
						}}
						autoCompleteName={AUTOCOMPLETE.PRODUCTS}
						columns={productAutocompleteColumns}
						selectionLabelAccessor={['SKU', 'name']}
					/>
				),
				showOnMobile: true
			},
			{
				header: 'quantity',
				accessorKey: 'quantity',
				size: 100,
				renderCell: ({ row: { index } }) => <IntegerInput name={`items[${index}].qty`} viewMode />,
				editCell: ({ row: { index } }) => <IntegerInput name={`items[${index}].qty`} />
			},
			{
				id: 'editRow',
				size: 20,
				maxSize: 20,
				showOnMobile: true
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20,
				showOnMobile: true
			}
		],
		[]
	);
	return (
		<Modal fullwidth maxWidth="medium" name={modalName}>
			<ModalTitle>
				<Typography variant="p1">{'easyPut'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="fromBin" colSpan={defaultColSpan} required>
						<AutoComplete
							name="fromBinID"
							autoCompleteName={AUTOCOMPLETE.BINS}
							setValue={(value) => {
								setValue('fromBinID', value?.ID, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('fromBinID')}
							columns={binAutocompleteColumns}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>
					<FormItem label="toBin" colSpan={defaultColSpan} required>
						<AutoComplete
							name="toBinID"
							autoCompleteName={AUTOCOMPLETE.BINS}
							setValue={(value) => {
								setValue('toBinID', value?.ID, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('toBinID')}
							columns={binAutocompleteColumns}
							selectionLabelAccessor={['Name']}
						/>
					</FormItem>

					<Table
						data={watch('items')}
						columns={columns}
						onRowDelete={({ index }) => remove(index)}
						onRowAdd={() => {
							append({});
						}}
						initEditMode={() => true}
						footerComponent={
							<div className="mt-4" style={{ display: 'flex' }}>
								<Button
									className="mx-3"
									variant="blackOutline"
									label="addNewRow"
									icon={faPlusCircle}
									onClick={() => {
										append({});
									}}
								/>
								<ImportByTemplate setValue={setValue} importActionDetails={productImportTemplate} />
							</div>
						}
					/>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label={'save'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					loading={loading}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default EasyputForm;
